import { getLanguage, type I18nLanguage } from '@/i18n';
import { type Locale, locales } from '@/utils/locale';
import { CalendarDate } from '@internationalized/date';
import {
  format as dateFnsFormat,
  formatDate,
  intervalToDuration,
  parseISO,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export const format = <DateType extends Date>(
  date: DateType | number | string,
  formatStr: string
) =>
  dateFnsFormat(date, formatStr, {
    locale: getDateFnsLocale(),
  });

export const formatFullDate = (date: Date) => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

export const formatDay = (date?: string) => {
  return date && formatDate(date, 'd MMM', { locale: getDateFnsLocale() });
};
export const formatYear = (date?: string) => {
  return date && formatDate(date, 'yyyy', { locale: getDateFnsLocale() });
};

export const TIME_ZONE_OFFSET_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/;

// Trip times arrive with their own offset (e.g. "12:00:00+02:00"). We show the
// wall-clock time as-is instead of converting to the browser timezone, so
// stripping the trailing offset lets us reinterpret it as UTC below.
export const stripTimeZoneOffset = (dateTimeStr: string): string =>
  (dateTimeStr ?? '').replace(TIME_ZONE_OFFSET_PATTERN, '');

// Format the trip's wall-clock time. We reinterpret the stripped (zone-less)
// value as a UTC instant and read its UTC fields directly, so the browser
// timezone — and any local DST transition — can never shift the displayed time.
export const formatTime = (date?: string) => {
  if (!date) {
    return date;
  }

  const parsed = new Date(`${stripTimeZoneOffset(date)}Z`);

  if (isNaN(parsed.getTime())) {
    return '';
  }

  const hours = String(parsed.getUTCHours()).padStart(2, '0');
  const minutes = String(parsed.getUTCMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

export const formatDateLLL = (date?: Date) => {
  return date && format(date, 'd MMM');
};

export const getDurationBetweenTimestamps = (
  first?: string,
  second?: string
) => {
  if (!first || !second) {
    return null;
  }

  const timeDiff = new Date(second).getTime() - new Date(first).getTime();

  if (timeDiff > 0) {
    const duration = intervalToDuration({ start: 0, end: timeDiff });
    return `${duration.hours ? duration.hours + 'h:' : ''}${duration.minutes}m`;
  }

  return null;
};

export const formatDuration = (
  duration: string | undefined
): string | undefined => {
  const parsedDuration = parseIsoDuration(duration);
  if (!parsedDuration) {
    return duration;
  }
  const { days, hours, minutes } = parsedDuration;
  const formattedDuration = [
    days ? `${days}d` : null,
    hours ? `${hours}h` : null,
    minutes ? `${minutes}m` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return formattedDuration || undefined;
};

export const isoDurationToMinutes = (duration?: string): number | undefined => {
  const parsedDuration = parseIsoDuration(duration);
  if (!parsedDuration) {
    return undefined;
  }
  const { days, hours, minutes, seconds } = parsedDuration;
  return days * 24 * 60 + hours * 60 + minutes + seconds / 60;
};

const parseIsoDuration = (duration?: string) => {
  if (!duration) {
    return undefined;
  }
  const match = duration.match(
    /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/
  );
  if (!match) {
    return undefined;
  }
  return {
    days: Number(match[1] ?? 0),
    hours: Number(match[2] ?? 0),
    minutes: Number(match[3] ?? 0),
    seconds: Number(match[4] ?? 0),
  };
};

export const formatDateWithDayOfWeek = (dateString?: string) => {
  return dateString && format(parseISO(dateString), 'EEE, d MMM yyyy');
};

export const isTimestampInPast = (
  departureTimeIsoString: string,
  timeZone: string
) => {
  const now = new Date();
  const nowInTimeZone = toZonedTime(now, timeZone);

  return new Date(departureTimeIsoString) < nowInTimeZone;
};

export const getDateFnsLocale = (): Locale => {
  const currentLanguage = getLanguage() as I18nLanguage;

  return locales[currentLanguage];
};

export function formatCountdown(
  countdown: { minutes: number; seconds: number } | null
): string {
  if (!countdown) {
    return '00:00';
  }
  const minutes = countdown.minutes.toString().padStart(2, '0');
  const seconds = countdown.seconds.toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

export function isoToCalendarDate(
  value: string | undefined
): CalendarDate | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (isNaN(d.getTime())) return undefined;
  return new CalendarDate(
    d.getUTCFullYear(),
    d.getUTCMonth() + 1,
    d.getUTCDate()
  );
}

export function calendarDateToIso(date: CalendarDate): string {
  return new Date(Date.UTC(date.year, date.month - 1, date.day)).toISOString();
}

// `<input type="date">` yields a yyyy-MM-dd value, but the profile/registration
// API fields are date-times. Append midnight; pass empty/undefined through.
export function isoDateToApiDateTime(date?: string): string | undefined {
  if (!date) return date;
  return `${date.split('T')[0]}T00:00:00`;
}
